// ** React Imports
import { lazy, Suspense, useEffect } from 'react';
// ** Router Components
import { BrowserRouter as AppRouter, Route, Switch, Redirect } from 'react-router-dom';

// ** Store
import { useDispatch, useSelector } from 'react-redux';
import { checkRememberedUserAC, getAllUserAC } from '@store/actions/auth';

// ** Routes & Default Routes
import { DefaultRoute, Routes } from './routes';
import DashboardLayout from '@layouts/dashboard';
import { getAllTournamentOptionsAC } from '@store/actions/tournament';

// ** NotFound Component
const NotFound = lazy(() => import('@pages/Page404'));

const Router = () => {
  // ** User redux
  const dispatch = useDispatch();
  const { isCheckingRememberedUser, isUserLoggedIn } = useSelector((state) => state.auth);

  useEffect(() => {
    const checkRemenberedUser = async () => {
      dispatch(checkRememberedUserAC());
    };
    checkRemenberedUser();
  }, [dispatch]);

  useEffect(() => {
    const getUserInfo = async () => {
      if (isUserLoggedIn && !isCheckingRememberedUser) {
        dispatch(getAllUserAC());
        dispatch(getAllTournamentOptionsAC());
      }
    };
    getUserInfo();
  }, [dispatch, isCheckingRememberedUser, isUserLoggedIn]);

  // ** Return Filtered Array of Routes & Paths
  const LayoutRoutesAndPaths = () => {
    const ListRoute = [];
    const Paths = [];

    if (Routes) {
      Routes.filter((route) => {
        ListRoute.push(route);
        Paths.push(route.path);
        return null;
      });
    }

    return { ListRoute, Paths };
  };

  /**
   ** Final Route Component Checks for Login & User Role and then redirects to the route
   */
  const FinalRoute = (props) => {
    const { route } = props;

    if (
      (!isCheckingRememberedUser && !isUserLoggedIn && route.meta === undefined) ||
      (!isCheckingRememberedUser && !isUserLoggedIn && route.meta && !route.meta.authRoute && !route.meta.publicRoute)
    ) {
      return <Redirect to="/login" />;
    }
    if (route.meta && route.meta.authRoute && isUserLoggedIn) {
      return <Redirect to="/" />;
    }
    return <route.component {...props} />;
  };

  // ** Return Route to Render
  const ResolveRoutes = () => {
    // ** Get Routes and Paths of the Layout
    const { ListRoute, Paths } = LayoutRoutesAndPaths();

    // ** We have freedom to display different layout for different route
    // ** We have made LayoutTag dynamic based on layout, we can also replace it with the only layout component,
    // ** that we want to implement like VerticalLayout or HorizontalLayout
    // ** We segregated all the routes based on the layouts and Resolved all those routes inside layouts

    // ** RouterProps to pass them to Layouts
    const routerProps = {};

    return (
      <Route path={Paths}>
        <Switch>
          {ListRoute.map((route) => {
            return (
              <Route
                key={route.path}
                path={route.path}
                exact={route.exact === true}
                render={(props) => {
                  // ** Assign props to routerProps
                  Object.assign(routerProps, {
                    ...props,
                    meta: route.meta,
                  });

                  return (
                    <>
                      {!!route.meta ? (
                        <Suspense fallback={null}>
                          <FinalRoute route={route} {...props} />
                        </Suspense>
                      ) : (
                        <DashboardLayout>
                          <Suspense fallback={null}>
                            <FinalRoute route={route} {...props} />
                          </Suspense>
                        </DashboardLayout>
                      )}
                    </>
                  );
                }}
              />
            );
          })}
        </Switch>
      </Route>
    );
  };

  return (
    <AppRouter basename={process.env.REACT_APP_BASENAME}>
      <Switch>
        {/* If user is logged in Redirect user to DefaultRoute else to login */}
        <Route
          exact
          path="/"
          render={() => {
            return isUserLoggedIn ? <Redirect to={DefaultRoute} /> : <Redirect to="/login" />;
          }}
        />

        {ResolveRoutes()}

        {/* NotFound Error page */}
        <Suspense fallback={null}>
          <Route path="*" component={NotFound} />
        </Suspense>
      </Switch>
    </AppRouter>
  );
};

export default Router;
